// Inline SOP field-level help text — lifted from the May 2026 Inventory Transfer
// SOP "Quick Field Reference". Surfaces "what / why" beside each form field so
// operators don't need to read the doc to fill the form correctly.
//
// Plain object — no imports, no JSX. Consumers pull strings via HELP.<bucket>.<field>
// and pass them into MUI <TextField helperText={...} /> or a Tooltip adornment.

export const HELP = {
  po: {
    warehouse_code:
      'Where the goods will physically land. Must be a real warehouse (Own / FBA / 3PL). Cannot be a virtual location (GIT / manufacturer / supplier).',
    transport_mode:
      'Air, Sea or Road. Drives expected transit days + which documents customs needs.',
    bill_of_lading:
      'B/L number (sea freight) or AWB (air freight). Issued by the forwarder once the shipment is booked.',
    port_of_loading:
      'Origin port the cargo departs from — e.g. Shanghai, Sydney, Mumbai.',
    port_of_discharge:
      'Destination port the cargo arrives at — e.g. Melbourne, Rotterdam, Los Angeles.',
    eta_port_discharge:
      'Expected arrival date at the destination port. Drives the in-transit dashboard ETA + landed-cost accruals.',
    incoterms:
      'FOB / CIF / DAP / DDP / EXW etc. Determines who pays freight, insurance and customs duty, and at which point title + risk transfer.',
    manufacturing_coo:
      'Country where the goods were manufactured (ISO-2 e.g. CN, IN, IT). Required on the commercial invoice for customs clearance.',
    internal_notes:
      'Free-text for internal audit references — e.g. the original Cin7 PO number if this is a migrated order.',
    supplier_invoice_number:
      'Factory invoice reference. Used by AP to three-way-match against the PO and the goods receipt.',
    supplier_po_ref:
      "The supplier's own reference number for this PO. Helpful when chasing the factory.",
    freight_cost:
      "Forwarder invoice amount for this shipment. Allocated to SKU lines on receive (landed cost method = 'by value'). Leave 0 here and enter on the transfer/receive if you don't yet have the invoice.",
    customs_duty:
      'Customs entry amount for this shipment. Allocated to SKU lines on receive. Leave 0 here and enter on the transfer/receive if customs has not yet cleared.',
    hsn: 'Harmonized System Code (6–10 digits) — used by customs to classify the product and calculate duty rates.',
    coo: 'Country of origin where this SKU was manufactured (ISO-2 e.g. CN, IN, IT). Falls back to the PO-level Manufacturing COO if left blank.',
    payment_terms:
      'Net 30 / Net 60 / 50% advance + 50% on shipment, etc. Drives the payment due date and AP aging report.',
    currency:
      'Currency of the PO and supplier invoice. Used for FX translation into the entity functional currency on receive.',
  },
  gr: {
    warehouse_code:
      'Where stock physically lands — REQUIRED. Defaults from the PO header. For legacy CIN7-imported POs that still ship to GIT (Goods In Transit — accounting bucket), this picker stays empty and an orange Alert appears: you MUST pick a real receiving warehouse (US-FBA / UK-FBA / 3PL etc.) before Submit becomes enabled. Server rejects empty, unknown, and GIT codes.',
    warehouse_code_per_line:
      'Per-line warehouse override. Defaults to the header value (shown as "(use header)"). Override a single line to ship part of this receipt to a DIFFERENT destination (split receipt): the system creates one GR per destination, all linked to the same PO. e.g. 60 units → US-FBA + 40 units → UK-FBA produces 2 GR rows.',
    received_quantity:
      'Actual quantity received per SKU line. Cannot exceed (ordered − already_received) — server rejects over-receipts with a clear error. To receive more than ordered, raise the PO line quantity first.',
    accepted_quantity:
      'Quantity accepted after QC inspection. Auto-calculated as received − rejected. Only accepted units create inventory batches; rejected units do NOT enter stock.',
    rejected_quantity:
      'Quantity rejected or damaged at inspection. Requires a Rejection Reason. Rejected units do not enter inventory.',
    receipt_date:
      'Actual physical arrival date (not the date the GR is keyed). Drives inventory aging.',
    batch_number:
      'Batch or lot number — required for traceability and potential recalls.',
    commercial_invoice_url: 'Attach the supplier commercial invoice PDF.',
    packing_list_url: 'Attach the supplier packing list PDF.',
    inspection_workflow:
      'After recording a receipt, open the GR detail and either Accept Receipt (full inspection pass) or Edit Inspection (per-line accept/reject). Inspection is per-GR — accepting one GR in a split receipt does NOT auto-accept its siblings.',
    delete_safety:
      'Delete only works on pending / non-inspected GRs whose batches have not been used. Once a GR is Accepted, deletion is blocked — reverse via inspection rejection or a counter-receipt instead.',
  },
  transfer: {
    freight_cost:
      "Forwarder invoice amount. Allocated to SKU lines on receive (landed cost method = 'by value').",
    customs_duty: 'Customs entry amount. Allocated to SKU lines on receive.',
    landed_cost_method:
      "'by_value' (default) allocates proportionally by line value. 'by_weight' uses gross_weight_kg.",
    transport_mode: 'Air, Sea or Road.',
    tracking_number: 'AWB or Bill of Lading number from the forwarder.',
    gross_weight_kg: 'Total shipment gross weight in kg. Required by customs.',
    description_of_goods:
      'Plain-language description for the forwarder and customs (not just SKU codes).',
  },
  ci: {
    seller_subsidiary:
      'Which legal entity is the seller of these goods. Drives the letterhead, tax id, and VAT number on the issued invoice.',
    override_consignee:
      "Use when the destination warehouse is a supplier / virtual location. Enter the real receiving party's name + address — customs needs this.",
    incoterms:
      'FOB / CIF / DAP / DDP / EXW. Should match the originating PO unless renegotiated.',
  },
};
